import React, { useEffect, useMemo, useState } from 'react';
import { reports } from '../../lib/api';
import {
  Bot,
  Calendar,
  CheckCircle2,
  Database,
  FileText,
  Link2,
  Loader2,
  MessageSquare,
  TrendingUp,
  Users,
} from 'lucide-react';

const RANGES = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y', days: 365 },
];

export default function Reports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await reports.overview(days);
        if (active) setData(res);
      } catch (err) {
        console.error('Reports load failed:', err);
        if (active) setError(err.message || 'Could not load reports.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [days]);

  const hasActivity = useMemo(() => {
    if (!data) return false;
    return [
      data.total_leads,
      data.leads_created,
      data.contacted,
      data.replied,
      data.inbound_messages,
      data.outbound_messages,
      data.bookings,
      data?.ai_drafts?.generated,
      data?.post_approvals?.pending,
      data?.post_approvals?.published,
    ].some((value) => Number(value || 0) > 0);
  }, [data]);

  return (
    <div className="app-page-shell bg-white" data-testid="reports-page">
      <header className="h-14 border-b border-[#E3E2E0] flex items-center justify-between px-6 sticky top-0 bg-white z-10">
        <h1 className="text-lg font-semibold text-[#191919]">Reports</h1>
        <div className="flex items-center gap-1 bg-[#FBFBFA] border border-[#E3E2E0] rounded-lg p-0.5" data-testid="reports-range">
          {RANGES.map((r) => (
            <button
              key={r.label}
              onClick={() => setDays(r.days)}
              className={`px-3 py-1 text-xs font-medium rounded-md ${
                days === r.days ? 'bg-white text-[#191919] shadow-sm' : 'text-[#6B6B6B] hover:text-[#191919]'
              }`}
              data-testid={`range-${r.label}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      <div className="app-page-scroll">
        <div className="max-w-6xl mx-auto p-6 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#6B6B6B]" />
          </div>
        ) : error ? (
          <div className="rounded-xl border border-[#FF8080] bg-[#FFEBE9] p-4 text-sm text-[#CE3636]">{error}</div>
        ) : data ? (
          <>
            <div className="flex items-center gap-2 text-xs text-[#6B6B6B]">
              <Database className="w-4 h-4" />
              <span>{data.data_source || 'Operational data'} over the last {data.window_days || days} days</span>
            </div>

            {!hasActivity && (
              <section className="border border-dashed border-[#E3E2E0] rounded-xl p-8 text-center">
                <Database className="w-8 h-8 mx-auto text-[#9B9B9B]" />
                <h2 className="mt-3 text-base font-semibold text-[#191919]">No operational activity yet</h2>
                <p className="mt-1 text-sm text-[#6B6B6B]">
                  Connect LinkedIn, import leads, send messages, or create approvals to populate these reports.
                </p>
              </section>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="reports-metrics">
              <Metric icon={Users} label="Total leads" value={data.total_leads} color="#0A0A0A" />
              <Metric icon={Users} label="New leads" value={data.leads_created} color="#0052CC" />
              <Metric icon={MessageSquare} label="Contacted" value={data.contacted} color="#B35900" />
              <Metric icon={CheckCircle2} label="Replied" value={data.replied} color="#1E7F4E" />
              <Metric icon={Calendar} label="Bookings" value={data.bookings} color="#6B00B3" />
              <Metric icon={TrendingUp} label="Response rate" value={`${data.response_rate || 0}%`} color="#0052CC" />
            </div>

            <Breakdown title="Inbox" icon={MessageSquare}>
              <StatRow label="Inbound messages" value={data.inbound_messages} />
              <StatRow label="Outbound messages" value={data.outbound_messages} />
              <StatRow label="Non-replied leads" value={data.non_replied} />
              <StatRow label="LinkedIn accounts" value={data.linkedin_accounts_connected} />
            </Breakdown>

            <Breakdown title="AI Drafts" icon={Bot}>
              <StatRow label="Generated" value={data?.ai_drafts?.generated || 0} />
              <StatRow label="Approved" value={data?.ai_drafts?.approved || 0} />
              <StatRow label="Rejected" value={data?.ai_drafts?.rejected || 0} />
              <StatRow label="Sent" value={data?.ai_drafts?.sent || 0} />
            </Breakdown>

            <Breakdown title="Approvals And Posts" icon={FileText}>
              <StatRow label="Pending" value={data?.post_approvals?.pending || 0} />
              <StatRow label="Approved" value={data?.post_approvals?.approved || 0} />
              <StatRow label="Rejected" value={data?.post_approvals?.rejected || 0} />
              <StatRow label="Published" value={data?.post_approvals?.published || 0} />
              <StatRow label="Failed" value={data?.post_approvals?.failed || 0} />
            </Breakdown>

            <Breakdown title="Bookings" icon={Link2}>
              <StatRow label="Scheduled" value={data?.bookings_breakdown?.scheduled || 0} />
              <StatRow label="Completed" value={data?.bookings_breakdown?.completed || 0} />
              <StatRow label="Cancelled" value={data?.bookings_breakdown?.cancelled || 0} />
              <StatRow label="Failed" value={data?.bookings_breakdown?.failed || 0} />
            </Breakdown>

            <section className="border border-[#E3E2E0] rounded-xl bg-white p-5">
              <h2 className="text-base font-semibold text-[#191919]">Leads added per day</h2>
              {(data.daily_leads || []).length === 0 ? (
                <p className="text-sm text-[#6B6B6B] mt-3">No leads added in this period.</p>
              ) : (
                <MiniBars points={data.daily_leads} />
              )}
            </section>
          </>
        ) : (
          <p className="text-sm text-[#6B6B6B]">Could not load reports.</p>
        )}
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, color }) {
  return (
    <div className="border border-[#E3E2E0] rounded-xl p-4 bg-white" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#6B6B6B]">{label}</span>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="mt-2 text-2xl font-semibold text-[#191919] tracking-tight">{value ?? 0}</div>
    </div>
  );
}

function Breakdown({ title, icon: Icon, children }) {
  return (
    <section className="border border-[#E3E2E0] rounded-xl bg-white p-5">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#6B6B6B]" />
        <h2 className="text-base font-semibold text-[#191919]">{title}</h2>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">{children}</div>
    </section>
  );
}

function StatRow({ label, value }) {
  return (
    <div>
      <p className="text-xs text-[#6B6B6B]">{label}</p>
      <p className="mt-1 text-xl font-semibold text-[#191919]">{value ?? 0}</p>
    </div>
  );
}

function MiniBars({ points }) {
  const max = Math.max(...points.map((p) => p.count), 1);
  return (
    <div className="mt-5 flex items-end gap-1 h-32" data-testid="daily-leads-chart">
      {points.map((p) => (
        <div key={p.date} className="flex-1 flex flex-col items-center gap-1">
          <div
            className="w-full bg-[#0A0A0A] rounded-t"
            style={{ height: `${(p.count / max) * 100}%`, minHeight: '2px' }}
            title={`${p.date}: ${p.count}`}
          />
          <span className="text-[10px] text-[#9B9B9B] whitespace-nowrap">{p.date.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}
